/**
 * Translates an Algolia `filters` string into a Typesense `filter_by` string.
 *
 * Supported Algolia syntax:
 *   attr:value, attr:'quoted value', attr:"quoted value", attr:true
 *   attr<10, attr <= 10, attr=10, attr!=10, attr>=10, attr>10
 *   attr:10 TO 20
 *   NOT <filter>, AND, OR, parentheses
 *
 * Strings that already use Typesense syntax (e.g. category `algoliaFilters` stored in
 * the Typesense collection: "marketing.productManufacturer.value:=`Robota`") are passed through.
 */

const TYPESENSE_SYNTAX = /:=|:!=|:\[|:[<>]|&&|\|\||`/

type Token =
  | { type: 'lparen' | 'rparen' | 'and' | 'or' | 'not' }
  | { type: 'filter', value: string }

const NUMBER = /^-?\d+(\.\d+)?$/

function formatValue(raw: string): string {
  // Numeric and boolean fields reject backtick-quoted values in Typesense
  if (NUMBER.test(raw)) return raw
  // Also when quoted: the hintamoottori quotes every value ('true'), and an unquoted single-word value
  // works for string fields too
  if (raw === 'true' || raw === 'false') return raw
  return '`' + raw.replace(/`/g, '') + '`'
}

function readQuoted(input: string, start: number): { value: string, end: number } {
  const quote = input[start]
  let i = start + 1
  let value = ''
  while (i < input.length && input[i] !== quote) {
    if (input[i] === '\\' && i + 1 < input.length) i++
    value += input[i]
    i++
  }
  return { value, end: i + 1 }
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < input.length) {
    const ch = input[i]
    if (/\s/.test(ch)) {
      i++
      continue
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen' })
      i++
      continue
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' })
      i++
      continue
    }

    const keyword = /^(AND|OR|NOT)(?=[\s(]|$)/.exec(input.slice(i))
    if (keyword) {
      tokens.push({ type: keyword[1].toLowerCase() as 'and' | 'or' | 'not' })
      i += keyword[1].length
      continue
    }

    // Attribute name (may itself be quoted)
    let attribute: string
    if (ch === '"' || ch === '\'') {
      const quoted = readQuoted(input, i)
      attribute = quoted.value
      i = quoted.end
    }
    else {
      const match = /^[^\s:<>=!()]+/.exec(input.slice(i))
      if (!match) throw new Error(`Cannot parse Algolia filter near "${input.slice(i)}"`)
      attribute = match[0]
      i += attribute.length
    }

    while (/\s/.test(input[i] || '')) i++

    const operator = /^(<=|>=|!=|<|>|=|:)/.exec(input.slice(i))?.[1]
    if (!operator) throw new Error(`Missing operator after "${attribute}" in Algolia filter`)
    i += operator.length
    while (/\s/.test(input[i] || '')) i++

    let value: string
    if (input[i] === '"' || input[i] === '\'') {
      const quoted = readQuoted(input, i)
      value = quoted.value
      i = quoted.end
    }
    else {
      const match = /^[^\s()]+/.exec(input.slice(i))
      value = match ? match[0] : ''
      i += value.length
    }

    if (operator === ':') {
      const range = /^\s+TO\s+(-?\d+(?:\.\d+)?)/.exec(input.slice(i))
      if (range && NUMBER.test(value)) {
        tokens.push({ type: 'filter', value: `${attribute}:[${value}..${range[1]}]` })
        i += range[0].length
        continue
      }
      tokens.push({ type: 'filter', value: `${attribute}:=${formatValue(value)}` })
      continue
    }

    const tsOperator = operator === '=' ? '=' : operator
    tokens.push({ type: 'filter', value: `${attribute}:${tsOperator}${value}` })
  }

  return tokens
}

const NEGATED_OPERATORS: Record<string, string> = {
  ':=': ':!=',
  ':!=': ':=',
  ':<': ':>=',
  ':<=': ':>',
  ':>': ':<=',
  ':>=': ':<'
}

function negate(filter: string): string {
  const range = /^(.+):\[(.+)\.\.(.+)\]$/.exec(filter)
  if (range) return `(${range[1]}:<${range[2]} || ${range[1]}:>${range[3]})`

  const match = /^([^:]+)(:!=|:<=|:>=|:=|:<|:>)(.*)$/.exec(filter)
  if (!match) throw new Error(`Cannot negate filter "${filter}"`)
  return `${match[1]}${NEGATED_OPERATORS[match[2]]}${match[3]}`
}

export function algoliaFilterToTypesense(filters?: string | null): string {
  const input = (filters || '').trim()
  if (!input) return ''
  if (TYPESENSE_SYNTAX.test(input)) return input

  const out: string[] = []
  let negateNext = false

  for (const token of tokenize(input)) {
    switch (token.type) {
      case 'lparen':
        if (negateNext) throw new Error('NOT before a parenthesised group is not supported')
        out.push('(')
        break
      case 'rparen':
        out.push(')')
        break
      case 'and':
        out.push('&&')
        break
      case 'or':
        out.push('||')
        break
      case 'not':
        negateNext = true
        break
      case 'filter':
        out.push(negateNext ? negate(token.value) : token.value)
        negateNext = false
        break
    }
  }

  return out.join(' ').replace(/\( /g, '(').replace(/ \)/g, ')')
}
